import { Injectable } from '@angular/core';
import { Heap, HEAP_SCRIPT_ID } from '@core/models/entity/heap.interface';
import { filter, map, mergeMap, Observable, switchMap } from 'rxjs';

import { AppSettingsService } from '../app-settings/app-settings.service';
import { PropertyBagService } from '../property-bag/property.bag.service';
import { UserService } from '../user/user.service';

declare const heap: Heap;

@Injectable({
  providedIn: 'root'
})
export class HeapService implements Heap {
  get heapScript(): Heap {
    return heap;
  }

  public isBatUser: boolean = false;

  constructor(
    private appSettingsService: AppSettingsService,
    private userService: UserService,
    private propertyBagService: PropertyBagService
  ) { }

  load(appId: string, isSsl: boolean): void {
    this.heapScript.load(appId, isSsl);
  }

  track(event: string, properties?: object): void {
    this.heapScript.track(event, properties);
  }

  identify(identity: string): void {
    this.heapScript.identify(identity);
  }

  resetIdentity(): void {
    this.heapScript.resetIdentity();
  }

  addUserProperties(properties: object): void {
    this.heapScript.addUserProperties(properties);
  }

  addEventProperties(properties: object): void {
    this.heapScript.addEventProperties(properties);
  }

  removeEventProperty(property: string): void {
    this.heapScript.removeEventProperty(property);
  }

  clearEventProperties(): void {
    this.heapScript.clearEventProperties();
  }

  initializeHeap(): Observable<void> {
    return this.initHeapScript().pipe(switchMap(() => this.initHeapUser()));
  }

  initHeapScript(): Observable<void> {
    return this.appSettingsService.appSettings$.pipe(
      filter((appSettings) => !!appSettings && !!appSettings.heap),
      map((appSettings) => {
        if (!appSettings.heap?.apiKey || !appSettings.heap?.isEnabled) {
          return;
        }

        if (document.getElementById(HEAP_SCRIPT_ID)) {
          return;
        }

        this.load(
          appSettings.heap?.apiKey,
          'https:' === document.location.protocol
        );
      })
    );
  }

  initHeapUser(): Observable<void> {
    return this.userService.user$.pipe(
      filter(user => !!user),
      mergeMap((user) => {
        return this.propertyBagService.isBat$.pipe(filter((isBatUser) => isBatUser != null),
          map((isBatUser => {
            this.identify(user.userId);
            this.addUserProperties({ sfContactId: user.salesForceMasterContactId });
            this.addUserProperties({ b2cId: user.b2cId });
            this.addUserProperties({ is_bat: isBatUser });
          })))
      }),
    );
  }
}
